import yup from "./yup-extended";

export const RealEstateBaseSchema = yup
  .object()
  .shape({
    if_own_real_estate: yup.string().required("Required")
  })
  .required();

export const RealEstateDetailsSchema = yup
  .object()
  .shape({
    street: yup.string().required("Required"),
    suburb: yup.string().required("Required"),
    state: yup.string().required("Required"),
    postcode: yup.string().integer(),
    value: yup.string().integer(),
    owner: yup.string().required("Required")
  })
  .required();

export const RealEstateMortgageSchema = yup
  .object()
  .shape({
    if_mortgage_on_property: yup.string().required("Required"),
    mortgage: yup.array().when(["if_mortgage_on_property"], {
      is: (if_mortgage_on_property: string) => if_mortgage_on_property === "yes",
      then: () =>
        yup
          .array()
          .of(
            yup.object().shape({
              lender: yup.string().required("Required"),
              value: yup.string().integer(),
              owner: yup.string().required("Required")
            })
          )
          .min(1, "please add at least one mortgage")
          .required("required"),
      otherwise: () =>
        yup.array().of(
          yup.object().shape({
            lender: yup.string(),
            value: yup.string(),
            owner: yup.string()
          })
        )
    }) as yup.ArraySchema<
      | {
          lender: string;
          value: string;
          owner: string;
        }[]
      | undefined,
      yup.AnyObject,
      undefined,
      ""
    >
  })
  .required();

export const RealEstateWhatWillHappenSchema = yup
  .object()
  .shape({
    property_status: yup.string().required("Required"),
    // sale flow
    sale_proceeds: yup.string().when("property_status", {
      is: "sell",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    sale_proceeds_owner: yup.string().when(["sale_proceeds"], {
      is: (sale_proceeds: string) => sale_proceeds === "one_keeps_all",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    split_by_percentage: yup.object().when(["sale_proceeds"], {
      is: (sale_proceeds: string) => sale_proceeds === "split_by_percentage",
      then: () =>
        yup.object().shape({
          user: yup.string().required("Required"),
          spouse: yup.string().required("Required")
        }),
      otherwise: () =>
        yup.object().shape({
          user: yup.string(),
          spouse: yup.string()
        })
    }) as yup.ObjectSchema<{ user: string; spouse: string }>,

    payments: yup.array().when(["sale_proceeds"], {
      is: (sale_proceeds: string) => sale_proceeds === "something_else",
      then: () =>
        yup
          .array()
          .of(
            yup
              .object()
              .shape({
                payment_to: yup.string(),
                other_person_entity: yup.string(),
                description: yup.string(),
                value: yup.string()
              })
              .required("Required")
          )
          .min(1, "at least one payment")
          .required("required"),
      otherwise: () => yup.array()
    }) as yup.ArraySchema<
      | {
          payment_to: string;
          other_person_entity: string;
          description: string;
          value: string;
        }[]
      | undefined,
      yup.AnyObject,
      undefined,
      ""
    >,
    split_balance: yup.object().when(["sale_proceeds"], {
      is: (sale_proceeds: string) => sale_proceeds === "something_else",
      then: () =>
        yup.object().shape({
          user: yup.string().required("Required"),
          spouse: yup.string().required("Required")
        }),
      otherwise: () => yup.object()
    }) as yup.ObjectSchema<{ user: string; spouse: string }>,

    //keeping flow
    property_keeper: yup.string().when(["property_status"], {
      is: (property_status: string) => property_status === "one_keeps",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),

    payer: yup.string().when(["property_status"], {
      is: (property_status: string) => property_status === "one_keeps",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    if_paying_settlement: yup.string().when(["property_status"], {
      is: (property_status: string) => property_status === "one_keeps",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    payment_method: yup.string().when(["if_paying_settlement"], {
      is: (if_paying_settlement: string) => if_paying_settlement === "yes",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    payment_by_fixed: yup.string().when(["payment_method"], {
      is: (payment_method: string) => payment_method === "fixed_amount",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    payment_by_percentage: yup.object().when(["payment_method"], {
      is: (payment_method: string) => payment_method === "percentage",
      then: () =>
        yup.object().shape({
          user: yup.string().required("Required"),
          spouse: yup.string().required("Required")
        }),
      otherwise: () => yup.object()
    }) as yup.ObjectSchema<{ user: string; spouse: string }>,
    payment_details: yup.string().when(["payment_method"], {
      is: (payment_method: string) => payment_method === "something_else",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    other_person_amount: yup.string().when(["payment_method"], {
      is: (payment_method: string) => payment_method === "something_else",
      then: () => yup.string().integer().defined(),
      otherwise: () => yup.string()
    })
  })
  .required();

export const RealEstatePaymentDetailsSchema = yup
  .object()
  .shape({
    payment_to: yup.string().required("Required"),
    other_person_entity: yup.string().when(["payment_to"], {
      is: (payment_to: string) => payment_to === "something_else",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    description: yup.string().required("Required"),
    value: yup.string().integer()
  })
  .required();

export const VehicleBaseSchema = yup
  .object()
  .shape({
    if_no_vehicle: yup.string()
  })
  .required();

export const VehicleSchema = yup.object().shape({
  make_model_year: yup.string().required("Required"),
  registration: yup.string().required("Required"),
  owner: yup.string().required("Required"),
  value: yup.string().integer().defined(),
  split_by: yup.string().required("Required"),
  sale_proceeds: yup.string().when("split_by", {
    is: (split_by: string) => split_by === "sell",
    then: () => yup.string().required("Required"),
    otherwise: () => yup.string()
  }),
  percentage: yup.object().when("split_by", {
    is: (split_by: string) => split_by === "percentage",
    then: schema =>
      schema.shape({
        user: yup.string().required("Required"),
        spouse: yup.string().required("Required")
      }),
    otherwise: schema =>
      schema.shape({
        user: yup.string(),
        spouse: yup.string()
      })
  }) as yup.ObjectSchema<{ user: string; spouse: string }>,
  comment: yup.string().notRequired()
});

export const BusinessBaseSchema = yup
  .object()
  .shape({
    if_own_business: yup.string().required("Required")
  })
  .required();

export const BusinessSchema = yup
  .object()
  .shape({
    name: yup.string().required("Required"),
    entity: yup.string().required("Required"),
    owner: yup.string().required("Required"),

    value: yup.string().integer().defined(),
    split_by: yup.string().required("Required"),
    sale_proceeds: yup.string().when(["split_by"], {
      is: (split_by: string) => split_by === "sell",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    percentage: yup.object().when(["split_by", "sale_proceeds"], {
      is: (split_by: string, sale_proceeds: string) => [split_by, sale_proceeds].includes("percentage"),
      then: schema =>
        schema.shape({
          user: yup.string().required("Required"),
          spouse: yup.string().required("Required")
        }),
      otherwise: schema =>
        schema.shape({
          user: yup.string(),
          spouse: yup.string()
        })
    }) as yup.ObjectSchema<{ user: string; spouse: string }>,
    comment: yup.string()
  })
  .required();

export const BankBaseSchema = yup.object().shape({
  if_no_bank: yup.string().defined()
});

export const BankSchema = yup.object().shape({
  name: yup.string().required("Required"),
  value: yup.string().integer().defined(),
  owner: yup.string().required("Required"),
  bsb: yup.string().integer().defined(),
  acc_number: yup.string().integer().defined(),
  split_by: yup.string().required("Required"),
  percentage: yup.object().when("split_by", {
    is: (split_by: string) => split_by === "percentage",
    then: schema =>
      schema.shape({
        user: yup.string().required("Required"),
        spouse: yup.string().required("Required")
      }),
    otherwise: schema =>
      schema.shape({
        user: yup.string(),
        spouse: yup.string()
      })
  }) as yup.ObjectSchema<{ user: string; spouse: string }>,
  comment: yup.string()
});

export const SuperannuationBaseSchema = yup
  .object()
  .shape({
    if_no_super: yup.string().notRequired()
  })
  .required();
export const SuperannuationDetailsSchema = yup
  .object()
  .shape({
    name: yup.string().required("Required"),
    if_self_managed: yup.string().notRequired(),
    value: yup.string().integer(),
    owner: yup.string().required("Required"),
    member_number: yup.string().when(["if_self_managed"], {
      is: (if_self_managed: string) => if_self_managed !== "yes",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    trustee_smsf: yup.string().when(["if_self_managed"], {
      is: (if_self_managed: string) => if_self_managed === "yes",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    details: yup.string().defined()
  })
  .required();

export const SuperannuationSplitSchema = yup
  .object()
  .shape({
    if_split_fund: yup.string().required("Required").nullable(),
    transfer_to: yup.string().when(["if_split_fund"], {
      is: (if_split_fund: string) => if_split_fund === "yes",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    if_percentage: yup.string().when(["if_split_fund"], {
      is: (if_split_fund: string) => if_split_fund === "yes",
      then: () => yup.string().notRequired(),
      otherwise: () => yup.string()
    }),

    transfer_percentage: yup.string().when(["if_percentage"], {
      is: (if_percentage: string) => if_percentage === "yes",
      then: () => yup.string().integer(),
      otherwise: () => yup.string()
    }),

    transfer_amount: yup.string().when(["if_split_fund", "if_percentage"], {
      is: (if_split_fund: string, if_percentage: string) => if_split_fund === "yes" && if_percentage !== "yes",
      then: () => yup.string().integer(),
      otherwise: () => yup.string()
    })
  })
  .required();

export const SuperannuationJointSchema = yup
  .object()
  .shape({
    joint_description: yup.string().required("Required"),
    spouse_receive_amount: yup.string().integerWithZero(),
    user_receive_amount: yup.string().integerWithZero()
  })
  .required();

export const InvestmentsBaseSchema = yup
  .object()
  .shape({
    if_own_investment: yup.string().required("Required")
  })
  .required();
export const InvestmentsSchema = yup
  .object()
  .shape({
    name: yup.string().required("Required"),
    owner: yup.string().required("Required"),
    value: yup.string().integer().defined(),
    split_by: yup.string().required("Required"),
    sale_proceeds: yup.string().when(["split_by"], {
      is: (split_by: string) => split_by === "sell",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    percentage: yup.object().when(["split_by", "sale_proceeds"], {
      is: (split_by: string, sale_proceeds: string) => [split_by, sale_proceeds].includes("percentage"),
      then: schema =>
        schema.shape({
          user: yup.string().required("Required"),
          spouse: yup.string().required("Required")
        }),
      otherwise: schema =>
        schema.shape({
          user: yup.string(),
          spouse: yup.string()
        })
    }) as yup.ObjectSchema<{ user: string; spouse: string }>,
    comment: yup.string().notRequired()
  })
  .required();

export const OverseasAssetBaseSchema = yup
  .object()
  .shape({
    if_own_asset_overseas: yup.string().required("Required")
  })
  .required();

export const OverseasAssetSchema = yup
  .object()
  .shape({
    name: yup.string().required("Required"),
    country: yup.string().required("Required"),
    asset_type: yup.string().required("Required"),
    owner: yup.string().required("Required"),
    value: yup.string().integer().defined(),
    split_by: yup.string().required("Required"),
    sale_proceeds: yup.string().when(["split_by"], {
      is: (split_by: string) => split_by === "sell",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    percentage: yup.object().when(["split_by", "sale_proceeds"], {
      is: (split_by: string, sale_proceeds: string) => [split_by, sale_proceeds].includes("percentage"),
      then: schema =>
        schema.shape({
          user: yup.string().required("Required"),
          spouse: yup.string().required("Required")
        }),
      otherwise: schema =>
        schema.shape({
          user: yup.string(),
          spouse: yup.string()
        })
    }) as yup.ObjectSchema<{ user: string; spouse: string }>,
    comment: yup.string()
  })
  .required();

export const FurnitureAndPersonalEffectsBaseSchema = yup
  .object()
  .shape({
    if_furniture_or_personal_effects: yup.string().required("Required")
  })
  .required();

export const FurnitureAndPersonalEffectsSchema = yup
  .object()
  .shape({
    name: yup.string().required("Required"),
    owner: yup.string().required("Required"),
    value: yup.string().integer().defined(),
    split_by: yup.string().required("Required"),
    sale_proceeds: yup.string().when(["split_by"], {
      is: (split_by: string) => split_by === "sell",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    percentage: yup.object().when(["split_by", "sale_proceeds"], {
      is: (split_by: string, sale_proceeds: string) => [split_by, sale_proceeds].includes("percentage"),
      then: schema =>
        schema.shape({
          user: yup.string().required("Required"),
          spouse: yup.string().required("Required")
        }),
      otherwise: schema =>
        schema.shape({
          user: yup.string(),
          spouse: yup.string()
        })
    }) as yup.ObjectSchema<{ user: string; spouse: string }>,

    comment: yup.string().notRequired()
  })
  .required();

export const FurnitureAndPersonalEffectsCategorySchema = yup
  .object()
  .shape({
    category: yup.string().required("Required"),
    description: yup.string().when("category", {
      is: "category_2",
      then: schema => schema.required("Required"),
      otherwise: () => yup.string()
    })
  })
  .required();

export const FurnitureAndPersonalEffectsValueSchema = yup
  .object()
  .shape({
    user_value: yup.string().integer(),
    spouse_value: yup.string().integer()
  })
  .required();

// ------------------------------------------------------------------

export const AdditionalRequirementsStep01Schema = yup
  .object()
  .shape({
    if_additional_payments: yup.string().required("Required"),
    payments: yup.array().when(["if_additional_payments"], {
      is: (if_additional_payments: string) => if_additional_payments === "yes",
      then: schema =>
        schema
          .of(
            yup
              .object()
              .shape({
                payor: yup.string(),
                payee: yup.string(),
                other_person_entity: yup.string(),
                description: yup.string(),
                value: yup.string()
              })
              .required("Required")
          )
          .min(1, "at least one payment is required")
          .required("required"),
      otherwise: schema =>
        schema.of(
          yup.object().shape({
            payor: yup.string(),
            payee: yup.string(),
            other_person_entity: yup.string(),
            description: yup.string(),
            value: yup.string()
          })
        )
    }) as yup.ArraySchema<
      | {
          value?: string | undefined;
          other_person_entity?: string | undefined;
          description: string;
          payor: string;
          payee: string;
        }[]
      | undefined,
      yup.AnyObject,
      undefined,
      ""
    >
  })
  .required();

export const AdditionalRequirementsPaymentSchema = yup
  .object()
  .shape({
    payor: yup.string().required("Required"),
    payee: yup.string().required("Required"),
    other_person_entity: yup.string().when(["payee"], {
      is: (payee: string) => payee === "something_else",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    }),
    description: yup.string().required("Required"),
    value: yup.string().integer()
  })
  .required();

export const AdditionalRequirementsStep02Schema = yup
  .object()
  .shape({
    if_dividing_assets_debts: yup.string().required("Required"),
    details: yup.string().when(["if_dividing_assets_debts"], {
      is: (if_dividing_assets_debts: string) => if_dividing_assets_debts === "yes",
      then: () => yup.string().required("Required"),
      otherwise: () => yup.string()
    })
  })
  .required();
